import { useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { Demande } from '../../../types';
import { formatDateLongue } from '../format';
import { complementCarte, etatFrise, phraseCarte, remplacantEnVigueur } from '../elementsJour';
import { MiniFrise } from '../MiniFrise';
import { PoleDot } from '../PoleDot';
import { StatutBadge } from '../StatutBadge';
import { ConfirmationEnvoiTipee } from '../GroupeAbsenceTipee';
import { useFenetreModale } from '../useFenetreModale';
import type { Seance } from './seancesDemande';
import { jourLong } from './dates';
import { horaireCourt } from '../horaire';

interface Props {
  seance: Seance | null;
  demande: Demande | null;
  libelleStatut?: string;
  /** La personne absente est la personne connectée : elle lit où en est sa demande, avec les mots de « Mes demandes ». */
  pourMoi: boolean;
  onFermer: () => void;
  /** Une demande a changé (envoi d'un brouillon repéré dans Tipee) : recharger. */
  onChange: () => void;
}

/** Où en est la demande, avec les mots de « Mes demandes ». La frise dit déjà « Remplacement effectué » : on ne le redit pas dessous. */
function EtatPourMoi({ d, maintenant }: { d: Demande; maintenant: Date }) {
  const frise = etatFrise(d.statut, d.date_atelier, maintenant);
  const phrase = phraseCarte(d, maintenant);
  const complement = complementCarte(d, maintenant);
  const redit = frise.phase != null && frise.termine;
  return (
    <div className="nd-apercu-etat">
      {frise.phase != null && <MiniFrise demande={d} peutGerer={false} />}
      {(!redit || complement) && (
        <p className="text-sm text-ink-1">
          {!redit && <b>{phrase}</b>}
          {complement ? `${redit ? '' : `${/[.…?!]$/.test(phrase) ? '' : '.'} `}${complement}` : ''}
        </p>
      )}
    </div>
  );
}

/**
 * Aperçu d'une demande déjà faite, PAR-DESSUS la page : on voit où elle en est sans quitter la saisie. Jamais de navigation qui remplace la page : « Ouvrir la
 * demande » s'ouvre dans un nouvel onglet. Un brouillon repéré dans Tipee peut être envoyé d'ici (le seul cas où la séance attend un geste de la personne).
 */
export function ApercuDemande({ seance, demande: d, libelleStatut, pourMoi, onFermer, onChange }: Props) {
  const [envoi, setEnvoi] = useState(false);
  const fermer = useRef<HTMLButtonElement>(null);
  const { ref } = useFenetreModale(() => { if (!envoi) onFermer(); }, { focusInitial: () => fermer.current, auDessus: () => envoi });
  const maintenant = new Date();
  const tipeeAEnvoyer = !!d && pourMoi && d.statut === 'brouillon' && d.source === 'tipee';
  const titre = d?.atelier_nom ?? seance?.atelierNom ?? 'Séance';

  return (
    <>
      <div className="modal-overlay">
        <div className="modal" role="dialog" aria-modal="true" aria-labelledby="nd-apercu-t" ref={ref}>
          <div className="modal-header">
            <h3 className="modal-title" id="nd-apercu-t">{d && <PoleDot couleur={d.pole_couleur} />} {titre}</h3>
            <button type="button" className="close-btn" aria-label="Fermer" onClick={onFermer}>✕</button>
          </div>
          <div className="modal-body">
            {!d ? (
              // Séance réservée à la gestion : rien d'autre n'est dit.
              <p className="text-sm text-ink-1">Cette séance est déjà prise en charge par l'équipe gestion : rien à faire de votre côté.</p>
            ) : (
              <>
                <p className="text-sm text-ink-3 tabular-nums mb-8">
                  {jourLong(String(d.date_atelier).slice(0, 10))}{horaireCourt(d.atelier_heure_debut, d.atelier_heure_fin) ? ` · ${horaireCourt(d.atelier_heure_debut, d.atelier_heure_fin)}` : ''}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''}
                </p>
                {pourMoi ? (
                  <EtatPourMoi d={d} maintenant={maintenant} />
                ) : (
                  <div className="flex items-center gap-8 mb-8"><StatutBadge statut={d.statut} libelle={libelleStatut} /></div>
                )}
                <dl className="nd-dl">
                  {d.absent_nom && <><dt>Absent·e</dt><dd>{d.absent_nom}</dd></>}
                  <dt>Remplaçant·e</dt><dd>{remplacantEnVigueur(d) ?? d.remplacant_nom ?? '—'}</dd>
                  {d.created_at && <><dt>Demandée le</dt><dd>{formatDateLongue(d.created_at)}</dd></>}
                  {d.notes && <><dt>Précisions</dt><dd>{d.notes}</dd></>}
                </dl>
              </>
            )}
          </div>
          <div className="modal-footer">
            {d && (
              <a className="btn btn-sm btn-ghost" href={`#demandes/${d.id}`} target="_blank" rel="noopener noreferrer">
                Ouvrir la demande dans un nouvel onglet <ExternalLink className="icon-sm" aria-hidden="true" />
              </a>
            )}
            {tipeeAEnvoyer && <button type="button" className="btn btn-sm btn-accent" onClick={() => setEnvoi(true)}>Envoyer ma demande de remplacement</button>}
            <button type="button" className="btn btn-sm" ref={fermer} onClick={onFermer}>Fermer</button>
          </div>
        </div>
      </div>
      {envoi && d && <ConfirmationEnvoiTipee demandes={[d]} onFermer={() => { setEnvoi(false); onFermer(); }} onChange={onChange} />}
    </>
  );
}
